import React, { useState } from 'react'
import { AlertTriangle, Loader2, X } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import useActivationStore from '../../stores/activationStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { Activation } from '../../types/activation'

interface DeleteActivationModalProps {
  isOpen: boolean
  onClose: () => void
  activation: Activation | null
}

const DeleteActivationModal: React.FC<DeleteActivationModalProps> = ({
  isOpen,
  onClose,
  activation
}) => {
  const { showSuccess, showError } = useToast()
  const { deleteActivation } = useActivationStore()
  const { selectedCustomer } = useGlobalCustomerStore()
  const [isDeleting, setIsDeleting] = useState(false)

  if (!isOpen || !activation) return null

  const handleDelete = async () => {
    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    setIsDeleting(true)

    try {
      await deleteActivation(activation.id, String(selectedCustomer.id))
      showSuccess('Activation Deleted', `"${activation.name}" has been deleted successfully`)
      onClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError('Failed to delete activation', errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Delete Activation
            </h3>

            <p className="text-sm text-gray-500 text-center mb-6">
              Are you sure you want to delete{' '}
              <span className="font-medium text-gray-700">"{activation.name}"</span>? This action
              cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteActivationModal
